import { z } from 'zod';

/**
 * Zod contracts for `src/shared/storage-services-api.ts`'s three endpoints (Phase 3.3 of
 * `docs/superpowers/plans/2026-10-14-harness/roadmap.md`, `docs/TECHNICAL-AUDIT.md` F-001/F-016).
 *
 * Written directly from `nexvia-api-web`'s real source (this repo's own prior hand-written
 * `ServiceOption`/`StorageServicesSelection` interfaces were NOT trusted as a source — see each
 * schema's own comment for exactly which file was read, this session, to derive it):
 *
 * - `nexvia-api-web/src/modules/storage/storage.controller.ts`'s `listServiceOptions` (line ~176)
 *   answers `res.status(200).json({ success: true, data })`, `data` being
 *   `storage-service-option.repository.ts`'s `findAllActive()` — a plain `repo.find(...)` over the
 *   `StorageServiceOptionEntity` (`.../entities/storage-service-option.entity.ts`), so the wire rows
 *   really carry `id`, `code`, `label`, `category`, `isActive`, `displayOrder`, `createdAt`,
 *   `updatedAt` — not just the three fields (`code`/`label`/`category`) this repo's old hand-written
 *   interface declared. Only those three are validated below (the ones this frontend actually
 *   reads); the rest pass through unvalidated-but-harmless, stripped by Zod's default
 *   unknown-key behavior, rather than being pinned for no consumer.
 *
 * - `GET /storage/me` (as read by `storage-services-api.ts`'s own `getMyServices()`) —
 *   `nexvia-api-web/src/modules/storage/handlers/get-storage-profile.handler.ts`, its own `return`
 *   statement at lines 42-54: `{ id, razonSocial, rut, email, phone, approvalStatus,
 *   rejectionReason, nextStep, breakdown }`. **Re-confirmed this session, line-by-line: it does NOT
 *   return `offeredServiceCodes` or `serviceConditions` at all, in any form.** This resolves F-016's
 *   stated question (was this ever wired?) with a plain NO, as of 2026-10-14 — not "not yet
 *   checked," an actual re-read of the handler's current body. Both fields stay
 *   optional/nullable below because that is what the real endpoint actually sends (nothing), not
 *   because of defensive style for its own sake.
 *
 *   [FIXED, 2026-10-14, same day as the finding above] `nexvia-api-web` does ship a dedicated,
 *   wired endpoint for exactly this data: `GET /storage/me/services`
 *   (`storage.routes.ts:74` -> `storage.controller.ts`'s `getMyServices` (line ~187) ->
 *   `get-storage-services.handler.ts`), answering `{ success: true, data: { serviceCodes,
 *   serviceConditions } }` — confirmed by reading `get-storage-services.handler.ts`'s own `return`
 *   (`{ serviceCodes: row?.offeredServiceCodes || [], serviceConditions: row?.serviceConditions ||
 *   null }`, always an array, never `null` — only `serviceConditions` can be `null`) and
 *   `update-storage-services.handler.ts`'s matching `return`/request-body field
 *   (`{serviceCodes?: string[], serviceConditions?: string|null}`, validated by
 *   `storage.schemas.ts`'s `updateStorageServicesBody`, which only recognizes
 *   `serviceCodes`/`serviceConditions` — the module used to PATCH `offeredServiceCodes`, which that
 *   schema's default "strip unknown keys" behavior silently dropped, not an error). Before this
 *   fix, `getMyServices` read bare `GET /storage/me` (never had this data) and `updateMyServices`
 *   PATCHed the wrong field name (silently ignored) — every account's offered-services selection
 *   read as empty always, and a save never actually persisted the chosen codes. Both functions now
 *   call the real endpoint/field name below; `StorageServicesSelection`'s own public shape
 *   (`offeredServiceCodes`/`serviceConditions`) is unchanged, so this folder's one consumer
 *   (`ServiciosPage.tsx`) and its tests needed no changes — only the wire-level translation did.
 */

/**
 * NEX-1165 — the catalog kinds (`storage_service_options.kind`, NEX-1166's
 * `1790146600000-AddStorageServiceOptionKind.js`, whose CHECK allows exactly these three):
 * - `service`: what a depósito offers and a client asks for / filters by;
 * - `condition`: what a depósito imposes, shown to the client next to the quote;
 * - `storage_type`: the client's own choice in the request (privado, compartido, otro, ...) — never
 *   something a depósito offers, so no panel screen lists it as a service.
 * A new kind is a migration on the API side; this enum changes with it.
 */
const StorageServiceOptionKindSchema = z.enum(['service', 'condition', 'storage_type']);
export type StorageServiceOptionKind = z.infer<typeof StorageServiceOptionKindSchema>;

/** `GET /storage/service-options` — one row of the backoffice-managed catalog, already unwrapped. */
export const StorageServiceOptionSchema = z.object({
  code: z.string(),
  label: z.string(),
  category: z.string().nullable(),
  kind: StorageServiceOptionKindSchema
});
export type StorageServiceOptionContract = z.infer<typeof StorageServiceOptionSchema>;

export const StorageServiceOptionListSchema = z.array(StorageServiceOptionSchema);

/**
 * `GET`/`PATCH /storage/me/services`'s shared `data` payload (both wrapped `{success,data}`) —
 * read verbatim off `get-storage-services.handler.ts`/`update-storage-services.handler.ts`'s own
 * `return` statements: `serviceCodes` is always an array (the handler defaults it with `|| []`,
 * never `null`); `serviceConditions` is `string | null` (defaults to `null`, not `''` — the
 * frontend's own `StorageServicesSelection` does that `''` default, not this schema).
 *
 * **Extended, 2026-10-14 (NEX-1151, "Condiciones que imponés por defecto" card)**:
 * `minContractMonths`/`noticeDays`/`quoteValidityDays` — the three account-level defaults the
 * Claude Design mockup's own card shows (mínimo de contratación, preaviso para la baja, vigencia
 * de las cotizaciones). Read verbatim off both handlers' `return` statements (re-confirmed this
 * session, not assumed): each is always present on the wire, `number | null` — the handler
 * defaults an unset column with `?? null`, deliberately not `|| null` (0 is a real value for all
 * three), so `.nullable()` without `.optional()` is the accurate contract, matching
 * `serviceConditions` above: a field the real backend always sends, even when unset.
 * `updateStorageServicesBody` (the REQUEST-side zod schema, `storage.schemas.ts`) only accepts a
 * non-negative integer or an absent key for each — never a literal `null` — so
 * `storage-services-api.ts`'s `updateMyServices()` omits a field entirely rather than sending
 * `null` for it; that is a request-shape concern, not this (response) schema's.
 */
export const StorageMyServicesDataSchema = z.object({
  serviceCodes: z.array(z.string()),
  serviceConditions: z.string().nullable(),
  minContractMonths: z.number().nullable(),
  noticeDays: z.number().nullable(),
  quoteValidityDays: z.number().nullable()
});
// No separately-exported inferred type here: storage-services-api.ts's own getMyServices() gets
// full type safety from TypeScript inferring parseContract<T>'s T straight off this schema at the
// call site — a standalone `z.infer<...>` export would have zero consumers (pnpm deadcode caught
// exactly that the first time this file existed).

/**
 * `POST /storage/service-options/suggest`'s `data` payload (wrapped `{success,data}`, 201) — a
 * depósito's suggestion for a service/condition option that is not in the admin-managed catalog
 * (mockup: the "Agregar otra opción" field + its "LISTA ADMINISTRABLE" note). Read verbatim off
 * `storage-service-option-suggestion.repository.ts`'s own `create()` return (confirmed against
 * that file and `storage-service-option-suggestion-http.int.test.js` this session): `id` and
 * `storageProfileId` are Postgres `SERIAL`/`INT` columns (plain JS numbers, not stringified
 * bigints), `label`/`status` are strings (`status` is always `"pending"` from this endpoint — the
 * CHECK constraint's other two values are only ever set by an admin-review flow that does not
 * exist yet), and `created_at` is a `TIMESTAMPTZ` that arrives as an ISO string once `res.json()`
 * serializes it.
 *
 * `ServiciosPage.tsx` does not read any of these fields back — it only needs to know the POST
 * succeeded — but the response is still parsed through this schema, not fire-and-forgotten, for
 * the same reason `StorageMyServicesDataSchema` is above and `updateMyServices()`'s own docblock
 * states: a 2xx response with a surprising shape must surface as `CONTRACT_VIOLATION`, not a
 * silent false success.
 */
export const StorageServiceOptionSuggestionDataSchema = z.object({
  id: z.number(),
  storageProfileId: z.number(),
  label: z.string(),
  status: z.string(),
  createdAt: z.string()
});
